import { config, useUserConfig } from '@littlebird/sdk';
import { observer } from 'mobx-react-lite';

import { GLOBAL_ASSISTANT_FILES_SOURCE } from '@/features/assistant-files/assistant-files-query';
import { AssistantFilesViewer } from '@/features/assistant-files/AssistantFilesViewer';
import { AssistantNotesEditor } from '@/features/assistant-notes';
import { EchoNotesViewer } from '@/features/echonotes/EchoNotesViewer';
import { FEATURES, useFeature } from '@/features/feature-flags';
import { settingsStore } from '@/stores/settings.store';
import { userStore } from '@/stores/user.store';
import { isMac } from '@/utils/capabilities';

import { SettingsSection } from '../settings-section';

import {
  CustomInstructionsTextArea,
  useChatPromptFields,
} from './chat-prompt-fields';
import { useEmailAttributionEnabled } from './email-attribution.hooks';

export const ChatSettings = observer(() => {
  const isInternal = userStore.isInternalUser;
  const hasFeature = useFeature();
  const echoNotesEnabled = isInternal;
  const fields = useChatPromptFields();
  const userConfig = useUserConfig();
  const emailAttributionEnabled = useEmailAttributionEnabled();

  return (
    <SettingsSection.Root>
      <SettingsSection.Header>
        <SettingsSection.Title>Chat</SettingsSection.Title>
      </SettingsSection.Header>
      <SettingsSection.Item
        direction="vertical"
        searchId="chat.custom-instructions"
      >
        <SettingsSection.ItemHeader>
          <SettingsSection.ItemLabel>
            Custom Instructions
          </SettingsSection.ItemLabel>
          <SettingsSection.ItemDescription>
            Personalize your interactions with Littlebird by providing your own
            instructions
          </SettingsSection.ItemDescription>
        </SettingsSection.ItemHeader>
        <CustomInstructionsTextArea
          className="min-h-[16rem] max-h-[32rem] text-sm resize-none [field-sizing:content]"
          fields={fields}
        />
      </SettingsSection.Item>
      <SettingsSection.Item
        direction="vertical"
        searchId="chat.assistant-notes"
      >
        <SettingsSection.ItemHeader>
          <SettingsSection.ItemLabel>Assistant Notes</SettingsSection.ItemLabel>
          <SettingsSection.ItemDescription>
            Review and edit what Littlebird has remembered from past chats to
            guide future conversations
          </SettingsSection.ItemDescription>
        </SettingsSection.ItemHeader>
        <AssistantNotesEditor className="h-[16rem] text-sm resize-none" />
      </SettingsSection.Item>
      {isInternal && (
        <SettingsSection.Item
          direction="vertical"
          searchId="chat.vfs-assistant-notes"
        >
          <SettingsSection.ItemHeader>
            <SettingsSection.ItemLabel>
              VFS extension of Assistant Notes
            </SettingsSection.ItemLabel>
            <SettingsSection.ItemDescription>
              Browse extended memory files stored in Littlebird&apos;s virtual
              filesystem
            </SettingsSection.ItemDescription>
          </SettingsSection.ItemHeader>
          <AssistantFilesViewer
            panelHeightClassName="h-[16rem]"
            source={GLOBAL_ASSISTANT_FILES_SOURCE}
          />
        </SettingsSection.Item>
      )}
      {echoNotesEnabled && (
        <SettingsSection.Item
          direction="vertical"
          searchId="chat.read-only-memory"
        >
          <SettingsSection.ItemHeader>
            <SettingsSection.ItemLabel>
              Read-Only Memory
            </SettingsSection.ItemLabel>
            <SettingsSection.ItemDescription>
              Knowledge and insights that Littlebird has learned from previous
              conversations
            </SettingsSection.ItemDescription>
          </SettingsSection.ItemHeader>
          <EchoNotesViewer />
        </SettingsSection.Item>
      )}
      <SettingsSection.Item searchId="chat.require-mod-enter-to-send">
        <SettingsSection.ItemHeader>
          <SettingsSection.ItemLabel>
            Require {isMac() ? '⌘' : 'Ctrl'}+Enter to Send
          </SettingsSection.ItemLabel>
          <SettingsSection.ItemDescription>
            When enabled, pressing Enter adds a new line and{' '}
            {isMac() ? '⌘' : 'Ctrl'}+Enter sends the message
          </SettingsSection.ItemDescription>
        </SettingsSection.ItemHeader>
        <SettingsSection.Switch
          checked={config.chat.require_mod_enter_to_send}
          onCheckedChange={(v) =>
            userConfig.chat.set('require_mod_enter_to_send', v)
          }
        />
      </SettingsSection.Item>
      <SettingsSection.Item searchId="chat.open-external-links">
        <SettingsSection.ItemHeader>
          <SettingsSection.ItemLabel>
            Open external links without confirmation
          </SettingsSection.ItemLabel>
          <SettingsSection.ItemDescription>
            Unsafe — Skips the warning before opening unknown potentially
            malicious links
          </SettingsSection.ItemDescription>
        </SettingsSection.ItemHeader>
        <SettingsSection.Switch
          checked={!config.chat.confirm_external_links}
          onCheckedChange={(v) =>
            userConfig.chat.set('confirm_external_links', !v)
          }
        />
      </SettingsSection.Item>
      <SettingsSection.Item searchId="chat.show-thoughts">
        <SettingsSection.ItemHeader>
          <SettingsSection.ItemLabel>Show Thoughts</SettingsSection.ItemLabel>
          <SettingsSection.ItemDescription>
            Show Littlebird&apos;s reasoning in the chat
          </SettingsSection.ItemDescription>
        </SettingsSection.ItemHeader>
        <SettingsSection.Switch
          checked={config.chat.show_thoughts}
          onCheckedChange={(v) => userConfig.chat.set('show_thoughts', v)}
        />
      </SettingsSection.Item>
      <SettingsSection.Item searchId="chat.previous-turn-preview">
        <SettingsSection.ItemHeader>
          <SettingsSection.ItemLabel>
            Show Previous-Turn Preview
          </SettingsSection.ItemLabel>
          <SettingsSection.ItemDescription>
            Keep part of the previous turn visible while Littlebird responds
          </SettingsSection.ItemDescription>
        </SettingsSection.ItemHeader>
        <SettingsSection.Switch
          checked={config.chat.previous_turn_preview}
          onCheckedChange={(v) =>
            userConfig.chat.set('previous_turn_preview', v)
          }
        />
      </SettingsSection.Item>
      {isInternal && (
        <SettingsSection.Item searchId="chat.past-screen-activity">
          <SettingsSection.ItemHeader>
            <SettingsSection.ItemLabel>
              Disable screen context retrieval
            </SettingsSection.ItemLabel>
            <SettingsSection.ItemDescription>
              Do not use information previously captured from your screen when
              answering.
            </SettingsSection.ItemDescription>
          </SettingsSection.ItemHeader>
          <SettingsSection.Switch
            checked={config.chat.disable_screen_context_search}
            onCheckedChange={(v) =>
              userConfig.chat.set('disable_screen_context_search', v)
            }
          />
        </SettingsSection.Item>
      )}
      <SettingsSection.Item searchId="chat.project-suggestions">
        <SettingsSection.ItemHeader>
          <SettingsSection.ItemLabel>
            Project Suggestions
          </SettingsSection.ItemLabel>
          <SettingsSection.ItemDescription>
            Suggest an existing or new project for a chat
          </SettingsSection.ItemDescription>
        </SettingsSection.ItemHeader>
        <SettingsSection.Switch
          checked={config.chat.project_suggestions_enabled}
          onCheckedChange={(v) =>
            userConfig.chat.set('project_suggestions_enabled', v)
          }
        />
      </SettingsSection.Item>
      {hasFeature(FEATURES.EMAIL_ATTRIBUTION) && (
        <SettingsSection.Item searchId="chat.email-attribution">
          <SettingsSection.ItemHeader>
            <SettingsSection.ItemLabel>
              Add “Sent via Littlebird” to emails
            </SettingsSection.ItemLabel>
            <SettingsSection.ItemDescription>
              Append a short Littlebird link to emails sent on your behalf
            </SettingsSection.ItemDescription>
          </SettingsSection.ItemHeader>
          <SettingsSection.Switch
            checked={emailAttributionEnabled}
            onCheckedChange={(v) =>
              userConfig.chat.set('email_attribution_footer_enabled', v)
            }
          />
        </SettingsSection.Item>
      )}
      {isInternal && (
        <SettingsSection.Item searchId="chat.cost-footer">
          <SettingsSection.ItemHeader>
            <SettingsSection.ItemLabel>
              Cost Footer (internal only)
            </SettingsSection.ItemLabel>
            <SettingsSection.ItemDescription>
              Show token + USD breakdown under the composer
            </SettingsSection.ItemDescription>
          </SettingsSection.ItemHeader>
          <SettingsSection.Switch
            checked={settingsStore.costFooterEnabled}
            onCheckedChange={(v) => settingsStore.setCostFooterEnabled(v)}
          />
        </SettingsSection.Item>
      )}
    </SettingsSection.Root>
  );
});
